// RisaProvider — app-level open/close state for the single R.I.S.A panel so one
// assistant instance serves every page (FRONTEND_RULES: one assistant, all
// screens). Pages call useRisa() to toggle it from their "Ask Risa" trigger and
// may pass a per-page `context` (greeting/insights/questions/renderers) that the
// panel renders. The shell (AppLayout) consumes isOpen to mount the panel and
// reflow the page beside it.
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

const RisaContext = createContext(null);

export function RisaProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [context, setContext] = useState(null);

  const open = useCallback((ctx = null) => {
    setContext(ctx);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  const toggle = useCallback((ctx = null) => {
    setIsOpen((v) => {
      if (!v) setContext(ctx);
      return !v;
    });
  }, []);

  const value = useMemo(
    () => ({ isOpen, context, open, close, toggle }),
    [isOpen, context, open, close, toggle],
  );

  return <RisaContext.Provider value={value}>{children}</RisaContext.Provider>;
}

RisaProvider.propTypes = { children: PropTypes.node };

export function useRisa() {
  const ctx = useContext(RisaContext);
  if (!ctx) throw new Error('useRisa must be used within a <RisaProvider>');
  return ctx;
}

export default RisaProvider;
